import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock, Check, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { passwordChecks, passwordProblem } from '@/lib/password-rules';

/**
 * POS-I185: set a new password from a reset link. The token is read from the address once, then removed
 * from the address bar (so it is not left in history or in a screenshot) and the page asks the browser
 * not to send a referrer, so the token cannot leak to any link on the page.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const token = useRef<string>(new URLSearchParams(window.location.search).get('token') ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkProblem, setLinkProblem] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);

  const checks = passwordChecks(password);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setLinkProblem(false);
    const problem = passwordProblem(password);
    if (problem) { setError(t(problem === 'common' ? 'passwordCommon' : 'passwordRules')); return; }
    if (password !== confirm) { setError(t('passwordsDontMatch')); return; }
    setPending(true);
    try {
      await api.post('/auth/reset-password', { token: token.current, newPassword: password });
      token.current = '';
      setDone(true);
    } catch (err) {
      const message = getApiErrorMessage(err, t('resetFailed'));
      // the server sentence for a bad link is English; show it in the chosen language
      const expired = message.startsWith('This link has expired') || /\btoken\b/i.test(message);
      setLinkProblem(expired);
      setError(expired ? t('resetExpired') : message);
    } finally {
      setPending(false);
    }
  };

  if (!token.current && !done) {
    return (
      <Card className="border-0 shadow-xl glass-card animate-scale-in">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-2xl font-bold">{t('resetTitle')}</CardTitle>
          <CardDescription>{t('resetInvalidLink')}</CardDescription>
        </CardHeader>
        <CardFooter className="flex flex-col gap-3 pt-2">
          <Link to="/forgot-password" className="w-full"><Button type="button" className="w-full h-11">{t('resetAskNew')}</Button></Link>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-xl glass-card animate-scale-in">
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-2xl font-bold animate-fade-in-up">{t('resetTitle')}</CardTitle>
        <CardDescription>{done ? t('resetDone') : t('resetSubtitle')}</CardDescription>
      </CardHeader>
      {done ? (
        <CardFooter className="flex flex-col gap-3 pt-2">
          <Link to="/login" className="w-full"><Button type="button" className="w-full h-11">{t('signIn')}</Button></Link>
        </CardFooter>
      ) : (
        <form onSubmit={submit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reset-password" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('newPassword')}
              </Label>
              <div className="relative">
                <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="reset-password"
                  type={show ? 'text' : 'password'}
                  className="ps-9 pe-10 h-11"
                  maxLength={72}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={show ? t('hidePassword') : t('showPassword')}
                  onClick={() => setShow(!show)}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <ul className="space-y-0.5 text-xs" aria-live="polite">
                {[
                  { ok: checks.basics, label: t('passwordHintBasics') },
                  { ok: checks.notCommon, label: t('passwordHintCommon') },
                ].map(({ ok, label }) => (
                  <li key={label} className={ok ? 'flex items-center gap-1.5 text-success-text' : 'flex items-center gap-1.5 text-muted-foreground'}>
                    {ok ? <Check className="h-3 w-3 shrink-0" /> : <Circle className="h-3 w-3 shrink-0" />}
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-confirm" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('confirmPassword')}
              </Label>
              <div className="relative">
                <Lock className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="reset-confirm"
                  type={show ? 'text' : 'password'}
                  className="ps-9 h-11"
                  maxLength={72}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive-text">
                {error}{' '}
                {linkProblem && (
                  <Link to="/forgot-password" className="font-medium underline">{t('resetAskNew')}</Link>
                )}
              </p>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-4 pt-2">
            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground font-semibold shadow-glow"
              disabled={pending || !password || !confirm}
            >
              {pending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t('resetButton')}
            </Button>
            <div className="flex justify-center pt-1">
              <LanguageSwitcher size="sm" />
            </div>
          </CardFooter>
        </form>
      )}
    </Card>
  );
}
